import { Alert, AlertColor, Avatar, Box, IconButton, Menu, MenuItem, Snackbar, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { GenericTable, IFooterData } from '../GenericTable/GenericTable';
import { GetOneOrganization, GetOrganizationGroup, GetOrganizationUserTrim, GetOrganizationUserTrimName } from '../../../../../services/OrganizationServices';
import { User } from '../../../../../model/OrganizationModel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoaderComp from '../../../../../components/Loader/LoaderComp';
import { DisableUserAndGroupModal } from '../DisableUserAndGroupModal/DisableUserAndGroupModal';
import { DeleteUser, ResetPassword, ResetPasswordLogged } from '../../../../../services/UserServices';
import { IResetPasswordLogged, PasswordReset, UserDelete } from '../../../../../model/UserModel';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { Group } from '../../../../../model/GroupModel';
import { useProfile } from '../../../../../context/ProfileProvider/useProfile';
import KeydriveSnackbar from '../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar';
import { KeyDriveChip } from '../../../../../components/KeydriveComponents/Tags/KeyDriveChip';
import { PaywallModal } from '../../../../../components/PaywallModal/PaywallModal';
import { PersonAdd } from '@mui/icons-material';
import useExtractPlan from '../../../../../hooks/useExtractPlan';
import { KeyDriveDialog } from '../../../../../components/KeydriveComponents/Dialog/KeyDriveDialog';
import { PersonRemove } from '@mui/icons-material';

interface IUserTable {
    nameOrg: string;
    search: string;
    isGroupPage: boolean;
    usersIdList?: string[];
    removeFromGroup?: (userId:string) => void;
    hasPhoneColumn?: boolean;
}

interface IMenuOptions {
    action: string;
    icon: JSX.Element;
    onClick: () => void;
}

interface IUserRow { 
    user: User; 
    nameOrg: string, 
    userGroups: Group[];
    setIsReload: React.Dispatch<React.SetStateAction<boolean>>;
    handleSnackbarWithMessage: (message: string, color: AlertColor) => void;
    isGroupPage: boolean;
    removeFromGroup: (userId: string) => void;
    hasPhoneColumn?: boolean;
}

type UserGroupsMap = { [userId: string]: Group[] };

const UserRow: React.FC<IUserRow> = ({ user, nameOrg, userGroups, setIsReload, handleSnackbarWithMessage, isGroupPage, removeFromGroup, hasPhoneColumn }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isModalDisableOpen, setIsModalDisableOpen] = useState<boolean>(false);
    const profile = useProfile();
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const {t} = useTranslation();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDialog = () => {
        setIsModalDisableOpen(!isModalDisableOpen);
    };

    const handleConfirmDisable = () => {
        const userDelete: UserDelete = {
            id: user.id,
            organizationName: nameOrg,
        }

        DeleteUser(userDelete)
        .then((data) => {
            setIsModalDisableOpen(false);
            handleSnackbarWithMessage(t("UserExcluido.message"), 'success')
            setIsReload(true);
        })
        .catch((err) => handleSnackbarWithMessage(err.response?.data || err.message || 'Erro ao excluir usuário', 'error'))
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const resetPassword = (email: string) => {
        const passwordReset: IResetPasswordLogged = {
            login: email,
            userLoggedEmail: profile.login!
        }

        ResetPasswordLogged(passwordReset)
        .then((data) => handleSnackbarWithMessage(t("AdminConsole.ResetPassword"), 'success'))
        .catch((err) => handleSnackbarWithMessage(err.response?.data || err.message || 'Erro ao resetar senha', 'error'));
    }

    const options: IMenuOptions[] = [
        ...(isGroupPage
            ? [{
                action: t("AdminConsole.RemoveGroup"),
                icon: <PersonRemoveIcon />,
                onClick:() => removeFromGroup(user.id),
            }]
            : []),
        {
            action: t("AdminConsole.DisableUser"),
            icon: <PersonOffIcon />,
            onClick: handleDialog,
        },
        {
            action: t("AdminConsole.EditUserInfo"),
            icon: <EditIcon />,
            onClick: () => navigate(`/${nameOrg}/adminConsole/usersAndGroups/newUser?userId=${user.id}`),
        },
        ...(!user.isGuest
            ? [{
            action: t("AdminConsole.ResetPassword"),
            icon: <RefreshIcon />,
            onClick: () => resetPassword(user.login),
        }]
            : []),
    ];

    return (
        <>
            <TableRow>
                <TableCell sx={{ padding: '0px' }}>
                    <IconButton
                        aria-label="more"
                        id="long-button"
                        aria-controls={open ? 'long-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={handleClick}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        id="long-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                    >
                        {options.map((option, index) => (
                            <MenuItem
                                key={index}
                                onClick={() => {
                                    option.onClick();
                                    handleClose();
                                }}
                                sx={{ gap: 1 }}
                            >
                                {option.icon}
                                {option.action}
                            </MenuItem>
                        ))}
                    </Menu>
                </TableCell>
                <TableCell sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    padding: '8px',
                 }}>
                    <Avatar sx={{ height: '26px', width: '26px' }} alt={user.name} />
                    <Tooltip title={user.name}>
                        <Typography component="span" sx={{
                            maxWidth: '300px',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden'
                        }}>
                            {user.name}
                            { user.isGuest && (
                                <KeyDriveChip 
                                    text={t("Signture.Guest")}
                                />
                            ) }
                        </Typography>
                    </Tooltip>
                </TableCell>
                {isGroupPage ? 
                    <>
                    <TableCell sx={{ padding: '8px' }}>{user.login}</TableCell>
                        {hasPhoneColumn ? (
                            <TableCell sx={{ padding: '8px' }}>{user.phone ? user.phone : "(00) 00000-0000"}</TableCell>
                            ): 
                            <></>
                        }
                    </> 
                    : (
                    <>
                    <Tooltip title={user.login}>
                        <TableCell sx={{ 
                            padding: '8px',
                            maxWidth: '210px',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden'
                        }}>{user.login}</TableCell>
                        </Tooltip>
                        <TableCell sx={{ padding: '8px' }}>{user.phone ? user.phone : "(00) 00000-0000"}</TableCell>
                        <TableCell sx={{ padding: '8px' }}>
                            <Typography sx={{ color: '#22638D',opacity: userGroups.length === 0 ? 0.5 : 1,}} >
                                {userGroups.length === 1 ? (
                                    <Typography component="span" onClick={() => navigate(`/${nameOrg}/adminConsole/usersAndGroups/${userGroups[0].id}`)} sx={{ "&:hover": { textDecoration: 'underline', cursor: 'pointer' } }}>
                                        {userGroups[0].name}
                                    </Typography>
                                ) : userGroups.length > 1 ? (
                                    <>
                                        <Typography component="span" onClick={() => navigate(`/${nameOrg}/adminConsole/usersAndGroups/${userGroups[0].id}`)} sx={{ "&:hover": { textDecoration: 'underline', cursor: 'pointer' }}}> 
                                            {userGroups[0].name}
                                        </Typography>
                                        <Typography sx={{marginLeft: "4px", "&:hover": { textDecoration: 'underline', cursor: 'pointer' } }} onClick={() => navigate(`/${nameOrg}/adminConsole/usersAndGroups/newUser?userId=${user.id}`)} component="span">
                                            + {userGroups.length - 1} 
                                        </Typography>
                                    </>
                                ) : (
                                        t("AdminConsole.DoesNotHave")
                                )}
                            </Typography>
                        </TableCell>
                    </>
                )}
            </TableRow>
            {/* <DisableUserAndGroupModal isopen={isModalDisableOpen} handleClickConfirm={handleConfirmDisable} handleOpenDialog={handleDialog} isUser /> */}
            <KeyDriveDialog 
                dialogTitleCenter
                 keyDriveDialogButtons={{
                    leftButtonMethod: () => { handleDialog() },
                    rightButtonMethod: () => {
                        handleConfirmDisable();
                        handleDialog();
                    },
                    rightButtonText: t("AdminConsole.Continue"),
                    leftButtonText: t("AdminConsole.Cancel")
                }}
                keyDriveDialogContent={{
                    dialogContent:t("AdminConsole.ConfirmIfYouReallyWantToDisableSelectedUser"),
                    dialogIcon: <PersonRemove sx={{ color: "#22638D", width: "44px", height: "44px" }}/>,
                    dialogTitle:t("AdminConsole.DisableModalTitle"),
                    handleTogglDialog: () => { handleDialog() },
                    openDialog: isModalDisableOpen
                }}
            />
        </>
    );
};

const UsersTable: React.FC<IUserTable> = ({ nameOrg, search, isGroupPage, usersIdList, removeFromGroup, hasPhoneColumn }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [totalUsers, setTotalUsers] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [userGroups, setUserGroups] = useState<UserGroupsMap>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [isReload, setIsReload] = useState<boolean>(false); 
    const [messageSnack, setMessageSnack] = useState<string | undefined>(undefined);
    const [colorSnack, setColorSnack] = useState<AlertColor | undefined>();
    const [limit] = useState<number>(50);
    const USERS_PER_PAGE = 5;
    const { t } = useTranslation();
    const headers = [
        t('AdminConsole.Name'),
        'E-mail',
        ...(hasPhoneColumn ? [t('AdminConsole.Phone')] : []),
        ...(isGroupPage? [] : [t('AdminConsole.Phone'), t('AdminConsole.Groups')]) 
    ];


    const handleSnackbarWithMessage = (message: string, color: AlertColor) => {
        setMessageSnack(message)
        setColorSnack(color)
    }

    useEffect(() => {
        if (nameOrg) {
            setLoading(true);
            const fetchUsers = search === '' ? GetOrganizationUserTrim : GetOrganizationUserTrimName;

            fetchUsers(nameOrg, 0, limit, search)
                .then((data) => {
                    if (isGroupPage || (usersIdList && usersIdList.length > 0)) {
                        const filteredUsers = data.filter(user => usersIdList!.includes(user.id));
                        setUsers(filteredUsers);
                        setTotalUsers(filteredUsers.length);
                    } else {
                        setUsers(data);
                        setTotalUsers(data.length);
                    }
                })
                .finally(() => {
                    setLoading(false);
                    setIsReload(false);
                });
        }
    }, [nameOrg, search, limit, isReload, isGroupPage, usersIdList]);

    useEffect(() => {
        if (users.length > 0 && nameOrg) {
            GetOrganizationGroup(nameOrg, 0, 50)
                .then((data) => {
                    const groupsMap: UserGroupsMap = {};
                    const usersMap = new Map(users.map(user => [user.id, user]));
                    
                    data.forEach((group) => {
                        const groupUsers = group.users; 
                        
                        if(groupUsers) {
                            groupUsers.forEach((userId) => {
                                const user = usersMap.get(userId);
                                if (user) {
                                    if (!groupsMap[user.id]) {
                                        groupsMap[user.id] = [];
                                    }
                                    groupsMap[user.id].push(group); 
                                }
                            });
                        }
                    });

                    setUserGroups(groupsMap);
                })
                .catch((error) => handleSnackbarWithMessage(error.response?.data || error.message || 'Erro ao carregar grupos', 'error'))
                .finally(() => setLoading(false));
        }
    }, [nameOrg, users]);

    const displayedUsers = users.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

    const footerData: IFooterData = {
        dataType: t('AdminConsole.UsersMin'),
        showingQuantity: displayedUsers.length,
        totalQuantity: totalUsers,
    };

    return (
        <>
            {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LoaderComp />
                </Box>
            ) : (
                <GenericTable
                    headers={headers}
                    bodyData={displayedUsers.map((user) => (
                        <UserRow removeFromGroup={removeFromGroup!} hasPhoneColumn={hasPhoneColumn} isGroupPage={isGroupPage} handleSnackbarWithMessage={handleSnackbarWithMessage} setIsReload={setIsReload} key={user.id} user={user} nameOrg={nameOrg} userGroups={userGroups[user.id] || []} />
                    ))}
                    isGroupPage={isGroupPage}
                    footerData={footerData}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    dataPerPage={USERS_PER_PAGE}
                />
            )}

            <KeydriveSnackbar 
                open={messageSnack != undefined}
                message={messageSnack}
                variant={colorSnack}
                onClose={() => setMessageSnack(undefined)}
            />
        </>
    );
};

export default UsersTable;
